import { deprecatedKinds, ElementChangeStatus, FormElement, FormElementKind, FormRevisionDiff, FormType } from '@gearbelt/global';
import { isEqual } from 'lodash';

// The elements the diff operates on — mirrors only `FormElementsRenderer`'s structural filters
// (deprecated kinds, RepairForm IssueSelect). The renderer's fill-time conditional-visibility filter
// is answer-dependent and does not apply here: a revision diff compares template structure with no
// answers, so evaluating visibility would drop every dependent field from the diff.
export function filterDiffableElements(elements: FormElement[], formType: FormType): FormElement[] {
  return elements.filter((element) => {
    if (deprecatedKinds.includes(element.kind)) {
      return false;
    }
    // Issue Select is hidden on repair templates (no historical answers in a preview).
    if (element.kind === FormElementKind.IssueSelect && formType === FormType.RepairForm) {
      return false;
    }
    return true;
  });
}

function hasStableId(element: FormElement): boolean {
  return typeof element.id === 'string' && element.id.length > 0;
}

// Drops elements without a stable id and de-duplicates repeated ids so a
// malformed revision can't corrupt the id-keyed diff below.
function sanitize(elements: FormElement[]): FormElement[] {
  const seen = new Set<string>();
  const valid: FormElement[] = [];

  for (const element of elements) {
    if (!hasStableId(element) || seen.has(element.id)) {
      continue;
    }
    seen.add(element.id);
    valid.push(element);
  }

  return valid;
}

// 0-based position of each element among only those present in BOTH versions,
// so added/removed elements don't shift a survivor's rank — a "move" is a change
// in order relative to the other surviving elements.
function buildIntersectionIndex(elements: FormElement[], otherIds: Set<string>): Map<string, number> {
  const index = new Map<string, number>();
  let position = 0;
  for (const element of elements) {
    if (otherIds.has(element.id)) {
      index.set(element.id, position);
      position++;
    }
  }
  return index;
}

// A surviving element counts as edited-in-place when its label, description, required flag,
// or config differs between revisions — this is what the diff's positional status (Added/
// Deleted/Moved) does not capture. Config is deep-compared; the scalar fields are compared
// directly (description normalises null/undefined to the same "absent").
function hasContentChanged(previous: FormElement, current: FormElement): boolean {
  return (
    previous.label !== current.label ||
    (previous.description ?? null) !== (current.description ?? null) ||
    previous.required !== current.required ||
    !isEqual(previous.config, current.config)
  );
}

export function compareFormRevisions(previous: FormElement[], current: FormElement[]): FormRevisionDiff {
  const prev = sanitize(previous);
  const curr = sanitize(current);

  const prevById = new Map(prev.map((element) => [element.id, element]));
  const currById = new Map(curr.map((element) => [element.id, element]));
  const prevIds = new Set(prevById.keys());
  const currIds = new Set(currById.keys());

  const prevRank = buildIntersectionIndex(prev, currIds);
  const currRank = buildIntersectionIndex(curr, prevIds);

  const diff: FormRevisionDiff = {};
  for (const id of new Set([...prevIds, ...currIds])) {
    if (!prevIds.has(id)) {
      diff[id] = { status: ElementChangeStatus.Added };
    } else if (!currIds.has(id)) {
      diff[id] = { status: ElementChangeStatus.Deleted };
    } else {
      const previousElement = prevById.get(id);
      const currentElement = currById.get(id);
      const contentChanged = !!previousElement && !!currentElement && hasContentChanged(previousElement, currentElement);
      const moveOffset = (currRank.get(id) ?? 0) - (prevRank.get(id) ?? 0);
      diff[id] =
        moveOffset === 0 ? { status: ElementChangeStatus.Unchanged, contentChanged } : { status: ElementChangeStatus.Moved, moveOffset, contentChanged };
    }
  }

  return diff;
}

// The move badge renders once, on the After column (which carries the full diff). In the
// Before column a moved element drops its badge but keeps its wrapper — blue if it was also
// edited, neutral grey otherwise — so both columns box every element uniformly and only the
// After side shows the arrow. Added/Deleted/Unchanged pass through unchanged.
export function splitDiffForColumns(diff: FormRevisionDiff): {
  before: FormRevisionDiff;
  after: FormRevisionDiff;
} {
  const before: FormRevisionDiff = {};
  for (const [id, change] of Object.entries(diff)) {
    before[id] = change.status === ElementChangeStatus.Moved ? { status: ElementChangeStatus.Unchanged, contentChanged: change.contentChanged } : change;
  }
  return { before, after: diff };
}
